import { useEffect } from 'react';

type SeoProps = {
  title: string;
  description: string;
  pathname?: string;
  image?: string;
  type?: 'website' | 'article' | 'profile' | 'product';
  noindex?: boolean;
  structuredData?: Record<string, unknown> | Record<string, unknown>[];
};

const SITE_NAME = 'Orvexaco';
const SITE_URL = 'https://www.orvexaco.com';
const DEFAULT_IMAGE = `${SITE_URL}/branding/logo_bg_white.svg`;

function imageMimeType(url: string) {
  const ext = url.split('?')[0].split('.').pop()?.toLowerCase();
  if (ext === 'svg') return 'image/svg+xml';
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
  if (ext === 'png') return 'image/png';
  if (ext === 'webp') return 'image/webp';
  return undefined;
}

function ensureMeta(attr: 'name' | 'property', key: string, content: string) {
  const selector = `${attr}="${key}"`;
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[${selector}]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

export function Seo({
  title,
  description,
  pathname = '/',
  image = DEFAULT_IMAGE,
  type = 'website',
  noindex = false,
  structuredData,
}: SeoProps) {
  useEffect(() => {
    const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
    const canonicalUrl = new URL(pathname, SITE_URL).toString();
    const robots = noindex ? 'noindex,nofollow' : 'index,follow';

    document.title = fullTitle;

    ensureMeta('name', 'description', description);
    ensureMeta('name', 'robots', robots);
    ensureMeta('name', 'theme-color', '#0A0A0A');
    ensureMeta('property', 'og:site_name', SITE_NAME);
    ensureMeta('property', 'og:title', fullTitle);
    ensureMeta('property', 'og:description', description);
    ensureMeta('property', 'og:type', type);
    ensureMeta('property', 'og:url', canonicalUrl);
    ensureMeta('property', 'og:image', image);
    const imageType = imageMimeType(image);
    if (imageType) ensureMeta('property', 'og:image:type', imageType);
    ensureMeta('property', 'og:image:alt', SITE_NAME);
    ensureMeta('name', 'twitter:card', 'summary_large_image');
    ensureMeta('name', 'twitter:title', fullTitle);
    ensureMeta('name', 'twitter:description', description);
    ensureMeta('name', 'twitter:image', image);

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    const existingScript = document.head.querySelector<HTMLScriptElement>('script[data-seo-jsonld="true"]');
    if (existingScript) {
      existingScript.remove();
    }

    if (structuredData) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-jsonld', 'true');
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }
  }, [description, image, noindex, pathname, structuredData, title, type]);

  return null;
}
